/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  X,
  Upload,
  Wrench,
  Zap,
  Leaf,
  HardHat,
  Droplets,
  Hammer,
  Users,
  Snowflake,
  MoreHorizontal,
  Home,
  Clock,
  CheckCircle2,
  RotateCcw,
  Pencil,
  Send,
  Star,
  Search,
  Image as ImageIcon,
  Film,
  Paperclip,
  ArrowLeft,
  Calendar,
  CheckSquare,
  Square,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { ticketsApi, PHASE_META, PRIORITY_META } from "./ticketsApi";
import "./TicketsTab.css";

const ALLOWED_IMAGE = new Set(["jpg", "jpeg", "png", "webp"]);
const ALLOWED_VIDEO = new Set(["mp4", "mov", "webm"]);
const ALLOWED = new Set([...ALLOWED_IMAGE, ...ALLOWED_VIDEO]);

const extOf = (name) => (name.split(".").pop() || "").toLowerCase();
const isVideoFile = (f) => extOf(f.name) ? ALLOWED_VIDEO.has(extOf(f.name)) : (f.type || "").startsWith("video/");

// Category icon name (stored on ticket_categories.icon) -> lucide. Falls
// back to the wrench when a category has no (or unknown) icon.
const CAT_ICONS = {
  "pipe-wrench": Wrench,
  flash: Zap,
  leaf: Leaf,
  HardHat,
  Droplets,
  Hammer,
  Users,
  snow: Snowflake,
  "ellipsis-horizontal": MoreHorizontal,
};
function catIcon(name) {
  return CAT_ICONS[name] || Wrench;
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Phase({ phase }) {
  const m = PHASE_META[phase] || { label: phase, cls: "" };
  return <span className={`tk-phase ${m.cls}`}>{m.label}</span>;
}

// Priority as a 3-bar signal-strength icon (P0 ▮▮▮ / P1 ▮▮▯ / P2 ▮▯▯).
// Colour conveys urgency; the text label is on hover (title) + aria.
const PRI_FILLED = { P0: 3, P1: 2, P2: 1 };
function Priority({ p }) {
  const m = PRIORITY_META[p] || { label: p, cls: "" };
  const filled = PRI_FILLED[p] || 1;
  return (
    <span
      className={`tk-prio ${m.cls}`}
      title={`${m.label} priority`}
      role="img"
      aria-label={`${m.label} priority`}
    >
      {[1, 2, 3].map((n) => (
        <i key={n} className={`tk-prio-bar ${n <= filled ? "on" : ""}`} />
      ))}
    </span>
  );
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// Compact assignee chip (avatar initials + name) for tiles and the detail.
function Assignee({ assignee, size = "sm" }) {
  if (!assignee) return null;
  return (
    <span className={`tk-assignee ${size}`} title={`Assigned to ${assignee.name}`}>
      <span className="tk-assignee-av">{initials(assignee.name)}</span>
      <span className="tk-assignee-name">{assignee.name}</span>
    </span>
  );
}

// Person identity: avatar (initials) + name, with an optional subtitle line
// (role / "raised <time>"). Used for the raiser and the assignee in the detail.
function PersonChip({ name, sub }) {
  if (!name) return <span className="tk-person-empty">{sub || "—"}</span>;
  return (
    <span className="tk-person">
      <span className="tk-person-av">{initials(name)}</span>
      <span className="tk-person-text">
        <span className="tk-person-name">{name}</span>
        {sub && <span className="tk-person-sub">{sub}</span>}
      </span>
    </span>
  );
}

function fmtDur(s) {
  if (s == null || Number.isNaN(s)) return "";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

// Relative time ("2h", "3d"); absolute shown on hover via the caller's title.
function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// SLA countdown relative to now → { overdue, text }.
function slaInfo(due) {
  if (!due) return null;
  const ms = new Date(due).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  const overdue = ms < 0;
  const abs = Math.abs(ms);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const text = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  return { overdue, text };
}

// Contact timings: days-of-week + a time window are friendlier than a fixed
// calendar slot for a maintenance visit.
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

// Build the free-text availability string from selected days + a time window.
// e.g. "Weekdays · 6:00 PM–8:00 PM", "Mon, Wed · from 9:00 AM", "Weekends".
function buildTimings(days, from, to) {
  let dayStr = "";
  const set = new Set(days);
  const isWeekdays = DOW.slice(0, 5).every((d) => set.has(d)) && !set.has("Sat") && !set.has("Sun");
  if (set.size === 7) dayStr = "Every day";
  else if (set.size === 5 && isWeekdays) dayStr = "Weekdays";
  else if (set.size === 2 && set.has("Sat") && set.has("Sun")) dayStr = "Weekends";
  else dayStr = DOW.filter((d) => set.has(d)).join(", ");

  let timeStr = "";
  if (from && to) timeStr = `${fmt12(from)}–${fmt12(to)}`;
  else if (from) timeStr = `from ${fmt12(from)}`;
  else if (to) timeStr = `until ${fmt12(to)}`;

  const parts = [dayStr, timeStr].filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
}

function VideoAttachment({ src }) {
  const [dur, setDur] = useState(null);
  return (
    <div className="tk-att-vwrap">
      <video
        className="tk-att-video"
        src={src}
        controls
        preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
      />
      {dur != null && <span className="tk-att-dur">{fmtDur(dur)}</span>}
    </div>
  );
}

// Render a comment's image/video attachments. Images open a lightbox via onZoom.
function Attachments({ items, onZoom }) {
  if (!items || !items.length) return null;
  return (
    <div className="tk-att-grid">
      {items.map((a) => {
        const src = ticketsApi.mediaSrc(a.url);
        return a.kind === "video" ? (
          <VideoAttachment key={a.id} src={src} />
        ) : (
          <button
            key={a.id}
            type="button"
            className="tk-att-img-link"
            onClick={() => onZoom && onZoom(src)}
          >
            <img className="tk-att-img" src={src} alt="attachment" loading="lazy" />
          </button>
        );
      })}
    </div>
  );
}

function Lightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="tk-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="tk-lightbox-x" onClick={onClose} aria-label="Close">
        <X size={22} />
      </button>
      <img src={src} alt="attachment" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

// Progress stepper, using the SAME phase vocabulary as the status pills:
// Not assigned → To do → In progress → Awaiting your OK (→ Closed).
const STEPPER = [
  ["triage", "Not assigned"],
  ["todo", "To do"],
  ["in_progress", "In progress"],
  ["owner_review", "Awaiting your OK"],
];
const PHASE_ORDER = ["triage", "todo", "in_progress", "owner_review", "closed"];
function Stepper({ phase }) {
  if (phase === "cancelled") {
    return <div className="tk-stepper"><span className="tk-step cancelled"><i className="tk-step-dot" /> Cancelled</span></div>;
  }
  const idx = phase === "closed" ? STEPPER.length : PHASE_ORDER.indexOf(phase);
  return (
    <div className="tk-stepper">
      {STEPPER.map((s, i) => (
        <span
          key={s[0]}
          className={`tk-step ${i < idx ? "done" : ""} ${i === idx ? "active" : ""}`}
        >
          <i className="tk-step-dot" />
          <span className="tk-step-label">{s[1]}</span>
        </span>
      ))}
    </div>
  );
}

const QUICK_REPLIES = [
  "On our way.",
  "Scheduled for tomorrow.",
  "Could you share a photo?",
  "Work completed — please confirm.",
];

// An open ticket is anything not yet closed/cancelled.
const isOpenPhase = (p) => p !== "closed" && p !== "cancelled";

export default function TicketsTab() {
  const { user, activeRole } = useAuth();
  const myId = user?.id;
  const isFM =
    activeRole === "FACILITY_MANAGER" ||
    activeRole === "ADMIN" ||
    (user?.is_admin === true) ||
    (Array.isArray(user?.app_roles) && user.app_roles.includes("FACILITY_MANAGER"));

  const [all, setAll] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selCat, setSelCat] = useState(null); // category_id | null
  const [selVilla, setSelVilla] = useState(null); // villa_number | null
  const [villaQuery, setVillaQuery] = useState("");
  const [selStatus, setSelStatus] = useState(null); // phase | null (= all)
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Bulk select (FM only)
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOp, setBulkOp] = useState(null); // "close" | "assign" | null
  const [bulkNote, setBulkNote] = useState("");
  const [bulkStaff, setBulkStaff] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState("");
  const [bulkDone, setBulkDone] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [data, catData] = await Promise.all([
        ticketsApi.list({}), // all statuses; filter client-side
        ticketsApi.listCategories().catch(() => ({ items: [] })),
      ]);
      setAll(data.items || []);
      setCats(catData.items || []);
    } catch (e) {
      setError(e.message || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Bulk-select helpers (FM only) ----
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
    setBulkOp(null);
    setBulkNote("");
    setBulkErr("");
    setBulkDone(0);
    setBulkStaff([]);
  };
  const doBulkClose = async () => {
    if (!bulkNote.trim()) return;
    setBulkBusy(true);
    setBulkErr("");
    setBulkDone(0);
    const ids = [...selected];
    let done = 0;
    for (const id of ids) {
      try { await ticketsApi.forceClose(id, bulkNote.trim()); } catch {}
      done++;
      setBulkDone(done);
    }
    await load();
    exitSelect();
    setBulkBusy(false);
  };
  const openBulkAssign = async () => {
    if (bulkStaff.length === 0) {
      const firstId = [...selected][0];
      try {
        const d = await ticketsApi.assignableStaff(firstId, true);
        const items = (d.items || []).slice().sort((a, b) => (b.is_self ? 1 : 0) - (a.is_self ? 1 : 0));
        setBulkStaff(items);
      } catch (e) {
        setBulkErr(e.message || "Could not load staff");
        return;
      }
    }
    setBulkOp("assign");
  };
  const doBulkAssign = async (staffId) => {
    setBulkBusy(true);
    setBulkErr("");
    setBulkDone(0);
    const ids = [...selected];
    let done = 0;
    for (const id of ids) {
      try { await ticketsApi.assign(id, staffId); } catch {}
      done++;
      setBulkDone(done);
    }
    await load();
    exitSelect();
    setBulkBusy(false);
  };

  // Category sidebar entries: every active category (the community's set),
  // with live ticket counts.
  const catEntries = useMemo(() => {
    const counts = {};
    all.forEach((t) => {
      counts[t.category_id] = (counts[t.category_id] || 0) + 1;
    });
    return cats
      .filter((c) => c.is_active !== false)
      .map((c) => ({ ...c, count: counts[c.id] || 0 }));
  }, [cats, all]);

  const villaEntries = useMemo(() => {
    const counts = {};
    all.forEach((t) => {
      const v = t.villa?.villa_number;
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([villa, count]) => ({ villa, count }))
      .sort((a, b) => a.villa.localeCompare(b.villa, undefined, { numeric: true }));
  }, [all]);

  // Status sidebar entries: every phase (same labels as the pills) + count.
  const statusEntries = useMemo(() => {
    const counts = {};
    all.forEach((t) => {
      counts[t.phase] = (counts[t.phase] || 0) + 1;
    });
    return Object.entries(PHASE_META).map(([phase, m]) => ({
      phase,
      label: m.label,
      cls: m.cls,
      count: counts[phase] || 0,
    }));
  }, [all]);

  const filtered = useMemo(
    () =>
      all.filter((t) => {
        if (selCat && t.category_id !== selCat) return false;
        if (selVilla && t.villa?.villa_number !== selVilla) return false;
        if (selStatus && t.phase !== selStatus) return false;
        return true;
      }),
    [all, selCat, selVilla, selStatus]
  );

  return (
    <div className={`tk-layout ${openId ? "tk-layout--detail" : ""}`}>
      {/* Sidebar: categories + villas — only for the list, hidden in detail */}
      {!openId && (
      <aside className="tk-side">
        <div className="tk-side-sec">
          <div className="tk-side-h">Status</div>
          <button
            className={`tk-side-item ${!selStatus ? "active" : ""}`}
            onClick={() => setSelStatus(null)}
          >
            <span className="tk-side-dot tk-side-dot--all" />
            <span className="tk-side-name">All statuses</span>
            <span className="tk-side-n">{all.length}</span>
          </button>
          {statusEntries.map((s) => (
            <button
              key={s.phase}
              className={`tk-side-item ${selStatus === s.phase ? "active" : ""}`}
              onClick={() => setSelStatus(selStatus === s.phase ? null : s.phase)}
            >
              <span className={`tk-side-dot ${s.cls}`} />
              <span className="tk-side-name">{s.label}</span>
              <span className="tk-side-n">{s.count}</span>
            </button>
          ))}
        </div>

        <div className="tk-side-sec">
          <div className="tk-side-h">Categories</div>
          <button
            className={`tk-side-item ${!selCat ? "active" : ""}`}
            onClick={() => setSelCat(null)}
          >
            <span className="tk-side-ic">
              <Wrench size={15} />
            </span>
            <span className="tk-side-name">All categories</span>
            <span className="tk-side-n">{all.length}</span>
          </button>
          {catEntries.map((c) => {
            const Ic = catIcon(c.icon);
            return (
              <button
                key={c.id}
                className={`tk-side-item ${selCat === c.id ? "active" : ""}`}
                onClick={() => setSelCat(selCat === c.id ? null : c.id)}
              >
                <span className="tk-side-ic">
                  <Ic size={15} />
                </span>
                <span className="tk-side-name">{c.label}</span>
                <span className="tk-side-n">{c.count}</span>
              </button>
            );
          })}
        </div>

        {villaEntries.length > 0 && (
          <div className="tk-side-sec">
            <div className="tk-side-h">Villa</div>
            {villaEntries.length > 1 && (
              <div className="tk-side-search">
                <Search size={13} />
                <input
                  type="search"
                  value={villaQuery}
                  onChange={(e) => setVillaQuery(e.target.value)}
                  placeholder="Search villa…"
                />
              </div>
            )}
            <button
              className={`tk-side-item ${!selVilla ? "active" : ""}`}
              onClick={() => setSelVilla(null)}
            >
              <span className="tk-side-ic">
                <Home size={15} />
              </span>
              <span className="tk-side-name">All villas</span>
            </button>
            {villaEntries
              .filter((v) =>
                v.villa.toLowerCase().includes(villaQuery.trim().toLowerCase())
              )
              .map((v) => (
                <button
                  key={v.villa}
                  className={`tk-side-item ${selVilla === v.villa ? "active" : ""}`}
                  onClick={() => setSelVilla(selVilla === v.villa ? null : v.villa)}
                >
                  <span className="tk-side-ic">
                    <Home size={15} />
                  </span>
                  <span className="tk-side-name">{v.villa}</span>
                  <span className="tk-side-n">{v.count}</span>
                </button>
              ))}
          </div>
        )}
      </aside>
      )}

      {/* Main: either the list, or the selected ticket's inline detail. */}
      <div className="tk-main">
        {openId ? (
          <TicketDetail
            id={openId}
            myId={myId}
            isFM={isFM}
            cats={cats}
            onBack={() => setOpenId(null)}
            onChanged={load}
          />
        ) : (
          <>
            <div className="tk-toolbar">
              <span className="tk-toolbar-count">
                {selStatus
                  ? `${PHASE_META[selStatus]?.label || selStatus} · ${filtered.length}`
                  : `${filtered.length} ${filtered.length === 1 ? "ticket" : "tickets"}`}
              </span>
              <div className="tk-toolbar-actions">
                {isFM && !selectMode && (
                  <button className="tk-select-btn" onClick={() => setSelectMode(true)}>
                    <CheckSquare size={14} /> Select
                  </button>
                )}
                {isFM && selectMode && (
                  <button className="tk-select-btn tk-select-btn--cancel" onClick={exitSelect}>
                    <X size={14} /> Cancel
                  </button>
                )}
                <button
                  className="tk-new-btn"
                  onClick={() => setCreateOpen(true)}
                >
                  <Plus size={15} /> Raise ticket
                </button>
              </div>
            </div>

            {isFM && selectMode && (
              <div className="tk-bulk-bar">
                <span className="tk-bulk-count">
                  {selected.size > 0
                    ? `${selected.size} selected`
                    : "Click rows to select"}
                </span>
                {selected.size > 0 && (
                  <>
                    <button
                      className="tk-bulk-btn"
                      onClick={() => setBulkOp("close")}
                      disabled={bulkBusy}
                    >
                      Force close
                    </button>
                    <button
                      className="tk-bulk-btn"
                      onClick={openBulkAssign}
                      disabled={bulkBusy}
                    >
                      Assign to…
                    </button>
                  </>
                )}
                {bulkErr && <span className="tk-bulk-err">{bulkErr}</span>}
              </div>
            )}

            {error && <div className="tk-error">{error}</div>}
            {loading ? (
              <div className="tk-empty">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="tk-empty">
                <Wrench size={28} strokeWidth={1.5} />
                <p>No tickets here. Raise one for any maintenance issue.</p>
              </div>
            ) : (
              <div className="tk-list">
                {filtered.map((t) => {
                  const Ic = catIcon(t.category_icon);
                  const isChecked = selected.has(t.id);
                  return (
                    <button
                      key={t.id}
                      className={`tk-row${isChecked ? " tk-row--selected" : ""}`}
                      onClick={() =>
                        selectMode ? toggleSelect(t.id) : setOpenId(t.id)
                      }
                    >
                      {selectMode && (
                        <span className="tk-row-check" aria-hidden="true">
                          {isChecked
                            ? <CheckSquare size={16} color="var(--accent)" />
                            : <Square size={16} color="#94a3b8" />}
                        </span>
                      )}
                      <span className="tk-row-icon">
                        <Ic size={16} />
                      </span>
                      <span className="tk-row-main">
                        <span className="tk-row-title">{t.title}</span>
                        <span className="tk-row-meta">
                          {t.category} · {t.villa?.villa_number || "—"} ·{" "}
                          {fmt(t.created_at)}
                        </span>
                        {t.assignee && <Assignee assignee={t.assignee} />}
                      </span>
                      <span className="tk-row-pills">
                        <Priority p={t.priority} />
                        <Phase phase={t.phase} />
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {createOpen && (
        <CreateTicketModal
          cats={cats}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}

      {bulkOp === "close" && (
        <div className="tk-bulk-modal" onClick={(e) => { if (e.target === e.currentTarget) setBulkOp(null); }}>
          <div className="tk-bulk-modal-body">
            <p className="tk-bulk-modal-title">
              Force-close {selected.size} ticket{selected.size !== 1 ? "s" : ""}
            </p>
            <textarea
              className="tk-bulk-note"
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Note required — reason for force-close"
              rows={3}
              disabled={bulkBusy}
            />
            {bulkBusy && (
              <p className="tk-bulk-progress">
                Closing {bulkDone}/{selected.size}…
              </p>
            )}
            <div className="tk-bulk-modal-foot">
              <button
                className="tk-btn-ghost"
                onClick={() => setBulkOp(null)}
                disabled={bulkBusy}
              >
                Cancel
              </button>
              <button
                className="tk-btn-primary"
                onClick={doBulkClose}
                disabled={bulkBusy || !bulkNote.trim()}
              >
                Close {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkOp === "assign" && (
        <div className="tk-bulk-modal" onClick={(e) => { if (e.target === e.currentTarget) setBulkOp(null); }}>
          <div className="tk-bulk-modal-body">
            <p className="tk-bulk-modal-title">
              Assign {selected.size} ticket{selected.size !== 1 ? "s" : ""} to…
            </p>
            <div className="tk-staff-list">
              {bulkStaff.length === 0 && (
                <em>No staff found.</em>
              )}
              {bulkStaff.map((s) => (
                <button
                  key={s.id}
                  disabled={bulkBusy}
                  onClick={() => doBulkAssign(s.id)}
                >
                  {s.is_self ? "Assign to me" : s.name} · {s.role_label}
                </button>
              ))}
            </div>
            {bulkBusy && (
              <p className="tk-bulk-progress">
                Assigning {bulkDone}/{selected.size}…
              </p>
            )}
            <button
              className="tk-btn-ghost"
              onClick={() => setBulkOp(null)}
              disabled={bulkBusy}
              style={{ marginTop: 8 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTicketModal({ cats, edit, priorityOnly = false, onClose, onCreated }) {
  const active = (cats || []).filter((c) => c.is_active !== false);
  const [title, setTitle] = useState(edit?.title || "");
  const [categoryId, setCategoryId] = useState(edit?.category_id || active[0]?.id || "");
  const [priority, setPriority] = useState(edit?.priority || "P2");
  const [description, setDescription] = useState(edit?.description || "");
  // Contact timings — days of week + a time window (friendlier than a date).
  const [days, setDays] = useState([]);
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const addFiles = (list) => {
    setError("");
    const arr = Array.from(list || []).filter((f) => {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.has(ext)) {
        setError(`${f.name}: unsupported type`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...arr]);
  };

  const submit = async () => {
    setError("");
    if (!priorityOnly && !title.trim()) return setError("Give the issue a short title");
    if (!priorityOnly && !categoryId) return setError("Pick a category");
    setBusy(true);
    try {
      const timings = buildTimings(days, fromTime, toTime);
      if (edit) {
        // The FM may only re-prioritise; the raiser edits the substance.
        await ticketsApi.update(
          edit.id,
          priorityOnly
            ? { priority }
            : {
                title: title.trim(),
                category_id: categoryId,
                priority,
                description: description.trim(),
                // only change timings when the resident sets them
                ...(timings ? { availability: timings } : {}),
              }
        );
        onCreated();
        return;
      }
      const ticket = await ticketsApi.create({
        title: title.trim(),
        category_id: categoryId,
        priority,
        description: description.trim() || undefined,
        availability: timings,
      });
      if (files.length) {
        setProgress({ done: 0, total: files.length });
        try {
          await ticketsApi.reply(ticket.id, { files });
        } catch (_) {}
      }
      onCreated();
    } catch (e) {
      setError(e.message || (edit ? "Could not save changes" : "Could not raise the ticket"));
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tk-modal-head">
          <h2>
            {priorityOnly
              ? "Change priority"
              : edit
              ? "Edit ticket"
              : "Raise a maintenance ticket"}
          </h2>
          <button onClick={onClose} className="tk-x">
            <X size={18} />
          </button>
        </div>
        <div className="tk-modal-body">
          {priorityOnly && (
            <div className="tk-field">
              <span>Ticket</span>
              <div className="tk-readonly">{edit?.title}</div>
            </div>
          )}

          {!priorityOnly && (
          <label className="tk-field">
            <span>Issue *</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Kitchen tap leaking"
            />
          </label>
          )}

          {!priorityOnly && (
          <div className="tk-field">
            <span>Category *</span>
            <div className="tk-chips">
              {active.map((c) => {
                const Ic = catIcon(c.icon);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`tk-chip ${categoryId === c.id ? "sel" : ""}`}
                    onClick={() => setCategoryId(c.id)}
                  >
                    <Ic size={13} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="tk-field">
            <span>Priority</span>
            <div className="tk-chips">
              {["P2", "P1", "P0"].map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`tk-chip ${priority === p ? "sel" : ""}`}
                  onClick={() => setPriority(p)}
                >
                  {PRIORITY_META[p].label}
                </button>
              ))}
            </div>
          </div>

          {!priorityOnly && (
          <label className="tk-field">
            <span>Describe the issue</span>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's wrong, where exactly…"
            />
          </label>
          )}

          {!priorityOnly && (
          <div className="tk-field">
            <span>When are you available? (contact timings)</span>
            <div className="tk-dow">
              {DOW.map((d) => (
                <button
                  type="button"
                  key={d}
                  className={`tk-dow-chip ${days.includes(d) ? "sel" : ""}`}
                  onClick={() =>
                    setDays((prev) =>
                      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
                    )
                  }
                >
                  {d}
                </button>
              ))}
            </div>
            <div className="tk-timerange">
              <input
                type="time"
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
                aria-label="From"
              />
              <span className="tk-timerange-sep">to</span>
              <input
                type="time"
                value={toTime}
                onChange={(e) => setToTime(e.target.value)}
                aria-label="To"
              />
            </div>
            {edit && edit.availability && (
              <small className="tk-field-hint">Currently: {edit.availability}</small>
            )}
          </div>
          )}

          {!edit && (
          <div className="tk-field">
            <span>Photos &amp; videos</span>
            <div
              className={`tk-drop ${dragOver ? "tk-drop--over" : ""}`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              role="button"
              tabIndex={0}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <Upload size={20} />
              <span>Drag &amp; drop, or click to add photos or videos</span>
            </div>
            {files.length > 0 && (
              <div className="tk-files">
                {files.map((f, i) => (
                  <div className="tk-fileitem" key={`${f.name}-${i}`}>
                    {isVideoFile(f) ? <Film size={14} /> : <ImageIcon size={14} />}
                    <span>{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {error && <div className="tk-error">{error}</div>}
        </div>
        <div className="tk-modal-foot">
          <button className="tk-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="tk-btn-primary" onClick={submit} disabled={busy}>
            {busy
              ? progress
                ? `Uploading ${progress.done}/${progress.total}…`
                : edit
                ? "Saving…"
                : "Submitting…"
              : edit
              ? "Save changes"
              : "Submit ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <span className="tk-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={n <= value ? "on" : ""}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={20} fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </span>
  );
}

function TicketDetail({ id, myId, isFM, cats, onBack, onChanged }) {
  const [t, setT] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [slaDate, setSlaDate] = useState("");
  const [showSla, setShowSla] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [staff, setStaff] = useState([]);
  const [allStaffLoaded, setAllStaffLoaded] = useState(false);
  const [notFixed, setNotFixed] = useState("");
  const [showNotFixed, setShowNotFixed] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [showClose, setShowClose] = useState(false);
  // resident close + ratings
  const [showRate, setShowRate] = useState(false);
  const [fmStars, setFmStars] = useState(0);
  const [workerStars, setWorkerStars] = useState(0);
  // FM override (force close / cancel) — shown as visible footer buttons.
  const [forceMode, setForceMode] = useState(null); // "close" | "cancel" | null
  const [forceNote, setForceNote] = useState("");
  const [zoom, setZoom] = useState(null); // lightbox image src
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setT(await ticketsApi.get(id));
    } catch (e) {
      setError(e.message || "Could not load ticket");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
      onChanged && onChanged();
    } catch (e) {
      setError(e.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  // Sort a self ("you") row to the top so "assign to me" is one click.
  const sortSelfFirst = (items) =>
    [...items].sort((a, b) => (b.is_self ? 1 : 0) - (a.is_self ? 1 : 0));

  const openAssign = async () => {
    try {
      const d = await ticketsApi.assignableStaff(id, false);
      setStaff(sortSelfFirst(d.items || []));
      setAllStaffLoaded(false);
      setShowAssign(true);
    } catch (e) {
      setError(e.message);
    }
  };

  // Fallback when no trade worker fits: show every staff member (incl. the FM
  // themselves + other FMs) by dropping the category trade filter.
  const loadAllStaff = async () => {
    try {
      const d = await ticketsApi.assignableStaff(id, true);
      setStaff(sortSelfFirst(d.items || []));
      setAllStaffLoaded(true);
    } catch (e) {
      setError(e.message);
    }
  };

  // Dismiss any open action dialog (backdrop click / Esc).
  const closeSheets = () => {
    setForceMode(null);
    setForceNote("");
    setShowSla(false);
    setShowAssign(false);
    setShowNotFixed(false);
    setShowClose(false);
    setShowRate(false);
  };

  const addFiles = (list) => {
    setError("");
    const arr = Array.from(list || []).filter((f) => {
      const ext = extOf(f.name);
      if (ext && !ALLOWED.has(ext)) {
        setError(`${f.name}: unsupported type`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...arr]);
  };

  const sendComment = () => {
    const body = reply.trim();
    if (!body && files.length === 0) return;
    act(() =>
      ticketsApi.reply(id, { body: body || undefined, files })
    ).then(() => {
      setReply("");
      setFiles([]);
    });
  };

  if (!t) {
    return <div className="tk-detail-pane tk-detail-loading">{error || "Loading…"}</div>;
  }

  const isRaiser = t.raised_by === myId;
  const isWorker = t.assignee && t.assignee.user_id === myId;
  const status = t.status;
  const isTerminal = t.phase === "closed" || t.phase === "cancelled";

  // Phases where the FM is still actively running the ticket (can assign a
  // worker and set/adjust the SLA — the two are independent of each other).
  const fmManaging = isFM && ["triage", "todo", "in_progress"].includes(t.phase);

  // Each action carries a `group`:
  //   "primary" → the one prominent button (the natural next step)
  //   "menu"    → folded into the "⋯ More" overflow so the header stays calm
  //   "danger"  → menu, styled as destructive (Cancel ticket)
  const actions = [];

  // --- worker ---
  if (isWorker && status === "assigned")
    actions.push({ key: "start", label: "Start work", icon: Wrench, group: "primary", onClick: () => act(() => ticketsApi.start(id)) });
  if (isWorker && status === "in_progress")
    actions.push({ key: "done", label: "Mark done", icon: CheckCircle2, group: "primary", onClick: () => act(() => ticketsApi.done(id)) });

  // --- FM: assign is the primary step while a ticket has no worker ---
  if (fmManaging)
    actions.push({
      key: "assign",
      label: t.assignee ? "Reassign worker" : "Assign worker",
      icon: Wrench,
      group: t.assignee ? "menu" : "primary",
      onClick: openAssign,
    });
  // FM: set / change the deadline — independent of assignment.
  if (fmManaging)
    actions.push({ key: "sla", label: t.sla_due_at ? "Change deadline" : "Set deadline", icon: Clock, group: "menu", onClick: () => { setSlaDate(""); setShowSla(true); } });

  // --- raiser ---
  // The raiser can close their own ticket at any stage. At "owner_review" the
  // close captures ratings (how it was handled); the same button works early.
  if (isRaiser && !isFM && !isTerminal)
    actions.push({ key: "close", label: "Close ticket", icon: CheckCircle2, group: "primary", onClick: () => setShowRate(true) });
  if (isRaiser && !isFM && t.phase === "owner_review")
    actions.push({ key: "notfixed", label: "Not fixed yet", icon: RotateCcw, group: "menu", onClick: () => setShowNotFixed(true) });

  // --- FM close: primary when the worker says they're done; otherwise a menu
  //     item (closing early is the exception, not the headline action). ---
  if (isFM && t.phase === "owner_review")
    actions.push({ key: "cob", label: "Close ticket", icon: CheckCircle2, group: "primary", onClick: () => setShowClose(true) });
  if (isFM && !isTerminal && t.phase !== "owner_review")
    actions.push({ key: "fclose", label: "Close ticket", icon: CheckCircle2, group: "menu", onClick: () => { setForceMode("close"); setForceNote(""); } });

  // --- edit: raiser edits everything; FM only re-prioritises ---
  if ((isRaiser || isFM) && !isTerminal)
    actions.push({ key: "edit", label: isFM && !isRaiser ? "Change priority" : "Edit ticket", icon: Pencil, group: "menu", onClick: () => setShowEdit(true) });

  // --- FM cancel: abandon a ticket that shouldn't be worked (duplicate, not
  //     needed). Kept distinct from Close and tucked into the menu. ---
  if (isFM && !isTerminal)
    actions.push({ key: "fcancel", label: "Cancel ticket", icon: X, group: "danger", onClick: () => { setForceMode("cancel"); setForceNote(""); } });

  if ((isRaiser || isFM) && isTerminal)
    actions.push({ key: "reopen", label: "Reopen ticket", icon: RotateCcw, group: "primary", onClick: () => act(() => ticketsApi.reopen(id)) });

  const primaryActions = actions.filter((a) => a.group === "primary");
  const menuActions = actions.filter((a) => a.group !== "primary");

  const Ic = catIcon(t.category_icon);

  // One chronological feed: lifecycle events + comments, oldest first. The
  // "created" event is shown in the header (raiser byline), so skip it here.
  const feed = [
    ...(t.timeline || [])
      .filter((e) => e.event !== "created")
      .map((e) => ({
        kind: "event",
        at: e.at,
        label: e.label || e.event,
        event: e.event,
      })),
    ...(t.replies || []).map((r) => ({ kind: "msg", at: r.created_at, r })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  const sla = slaInfo(t.sla_due_at);

  return (
    <>
      <button className="tk-back" onClick={onBack}>
        <ArrowLeft size={16} /> Back to tickets
      </button>
      <div className="tk-detail-pane">
      {(() => {
        const sheetOpen =
          forceMode || showSla || showAssign || showNotFixed || showClose || showRate;

        return (
          <>
            {/* ============ HEADER (clean, neutral) ============ */}
            <div className="tk-head">
              <span className="tk-head-ic">
                <Ic size={18} />
              </span>
              <div className="tk-head-main">
                <div className="tk-head-titlerow">
                  <h2 className="tk-head-title">{t.title}</h2>
                  <Phase phase={t.phase} />
                </div>
                <div className="tk-head-meta">
                  <span className="tk-head-who">{t.raiser?.name || "—"}</span>
                  <span className="tk-dot">·</span>
                  <span title={fmt(t.created_at)}>{timeAgo(t.created_at)}</span>
                  <span className="tk-dot">·</span>
                  <span className="tk-head-chip">
                    <Ic size={12} /> {t.category}
                  </span>
                  <span className="tk-head-chip">
                    <Home size={12} /> {t.villa?.villa_number}
                  </span>
                  {t.sla_due_at && sla && (
                    <span className={`tk-sla-chip ${sla.overdue ? "overdue" : ""}`}>
                      <Clock size={12} />{" "}
                      {sla.overdue ? `Overdue ${sla.text}` : `${sla.text} left`}
                    </span>
                  )}
                </div>
              </div>
              {actions.length > 0 && (
                <div className="tk-head-actions">
                  {primaryActions.map((a) => (
                    <button
                      key={a.key}
                      className="tk-btn-primary"
                      disabled={busy}
                      onClick={a.onClick}
                    >
                      <a.icon size={15} /> {a.label}
                    </button>
                  ))}
                  {menuActions.length > 0 && (
                    <div className="tk-more">
                      <button
                        className="tk-btn-secondary tk-more-btn"
                        disabled={busy}
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        title="More actions"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                      {menuOpen && (
                        <>
                          <div
                            className="tk-more-backdrop"
                            onClick={() => setMenuOpen(false)}
                          />
                          <div className="tk-more-menu" role="menu">
                            {menuActions.map((a) => (
                              <button
                                key={a.key}
                                role="menuitem"
                                className={`tk-more-item ${
                                  a.group === "danger" ? "tk-more-item--danger" : ""
                                }`}
                                disabled={busy}
                                onClick={() => {
                                  setMenuOpen(false);
                                  a.onClick();
                                }}
                              >
                                <a.icon size={15} /> {a.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ============ STEPPER STRIP ============ */}
            <div className="tk-stepper-strip">
              <Stepper phase={t.phase} />
            </div>

            {/* ============ BODY ============ */}
            <div className="tk-detail-grid2">
              {/* ---------- Conversation column ---------- */}
              <div className="tk-convo">
                <div className="tk-feed">
                  {/* opening post = the first report; aligns right when the
                      viewer is the raiser (their own message) */}
                  {t.description &&
                    (() => {
                      const openingMine = !!myId && t.raiser?.id === myId;
                      return (
                        <div
                          className={`tk-msg tk-msg--opening ${
                            openingMine ? "tk-msg--mine" : ""
                          }`}
                        >
                          {!openingMine && (
                            <span className="tk-msg-av">
                              {initials(t.raiser?.name)}
                            </span>
                          )}
                          <div className="tk-msg-bubble">
                            <span className="tk-msg-tag">First report</span>
                            <div className="tk-msg-head">
                              <strong>{t.raiser?.name || "—"}</strong>
                              <span title={fmt(t.created_at)}>
                                {timeAgo(t.created_at)}
                              </span>
                            </div>
                            <div className="tk-msg-body">{t.description}</div>
                          </div>
                        </div>
                      );
                    })()}

                  {(() => {
                    let lastDay = null;
                    const today = new Date().toDateString();
                    const yesterday = new Date(Date.now() - 86400000).toDateString();
                    const dayLabel = (iso) => {
                      const ds = new Date(iso).toDateString();
                      if (ds === today) return "Today";
                      if (ds === yesterday) return "Yesterday";
                      return new Date(iso).toLocaleDateString(undefined, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      });
                    };
                    return feed.map((item, i) => {
                      const day = new Date(item.at).toDateString();
                      let sep = null;
                      if (day !== lastDay) {
                        lastDay = day;
                        sep = (
                          <div className="tk-day-chip" key={`d-${i}`}>
                            {dayLabel(item.at)}
                          </div>
                        );
                      }

                      if (item.kind === "event") {
                        const key =
                          item.event === "closed" || item.event === "worker_done";
                        return (
                          <div key={`e-${i}`} style={{ display: "contents" }}>
                            {sep}
                            <div className={`tk-event ${key ? "tk-event--key" : ""}`}>
                              <span className="tk-event-pill">
                                <span className="tk-event-dot" />
                                <span className="tk-event-label">{item.label}</span>
                                <span className="tk-event-at" title={fmt(item.at)}>
                                  {timeAgo(item.at)}
                                </span>
                              </span>
                            </div>
                          </div>
                        );
                      }

                      const mine = item.r.author?.id === myId;
                      return (
                        <div key={item.r.id} style={{ display: "contents" }}>
                          {sep}
                          <div className={`tk-msg ${mine ? "tk-msg--mine" : ""}`}>
                            {!mine && (
                              <span className="tk-msg-av">
                                {initials(item.r.author?.name)}
                              </span>
                            )}
                            <div className="tk-msg-bubble">
                              <div className="tk-msg-head">
                                <strong>{item.r.author?.name || "—"}</strong>
                                <span title={fmt(item.r.created_at)}>
                                  {timeAgo(item.r.created_at)}
                                </span>
                              </div>
                              {item.r.body && (
                                <div className="tk-msg-body">{item.r.body}</div>
                              )}
                              <Attachments items={item.r.attachments} onZoom={setZoom} />
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {feed.length === 0 && !t.description && (
                    <div className="tk-thread-empty">
                      No messages yet. Add the first update — a photo helps the team a
                      lot.
                    </div>
                  )}
                </div>

                {/* quick replies (FM) — float above composer */}
                {isFM && !isTerminal && !sheetOpen && (
                  <div className="tk-qr">
                    {QUICK_REPLIES.map((q) => (
                      <button
                        key={q}
                        type="button"
                        className="tk-qr-chip"
                        onClick={() => setReply(q)}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {error && <div className="tk-error">{error}</div>}

                {/* frosted floating composer (hidden while a sheet is open) */}
                {!isTerminal && !sheetOpen && (
                  <div className="tk-composer">
                    {files.length > 0 && (
                      <div className="tk-composer-files">
                        {files.map((f, i) => (
                          <div className="tk-fileitem" key={`${f.name}-${i}`}>
                            {isVideoFile(f) ? (
                              <Film size={14} />
                            ) : (
                              <ImageIcon size={14} />
                            )}
                            <span>{f.name}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setFiles((p) => p.filter((_, j) => j !== i))
                              }
                            >
                              <X size={13} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tk-composer-row">
                      <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        hidden
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                      <button
                        className="tk-attach-btn"
                        title="Attach photos or videos"
                        onClick={() => fileRef.current?.click()}
                        disabled={busy}
                      >
                        <Paperclip size={16} />
                      </button>
                      <input
                        className="tk-composer-input"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Add an update…"
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !e.shiftKey &&
                            (reply.trim() || files.length)
                          ) {
                            e.preventDefault();
                            sendComment();
                          }
                        }}
                      />
                      <button
                        className="tk-btn-primary"
                        disabled={busy || (!reply.trim() && files.length === 0)}
                        onClick={sendComment}
                      >
                        <Send size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {/* ----- ACTION DIALOGS (centered modal + backdrop) ----- */}
                {sheetOpen && (
                  <div className="tk-sheet-backdrop" onClick={closeSheets} />
                )}
                {forceMode && (
                  <div className="tk-sheet">
                    <span className="tk-sheet-title">
                      {forceMode === "close" ? "Force-close" : "Force-cancel"} reason
                      (required)
                    </span>
                    <input
                      value={forceNote}
                      onChange={(e) => setForceNote(e.target.value)}
                      placeholder="Why are you overriding the workflow?"
                    />
                    <button
                      className="tk-btn-primary"
                      disabled={!forceNote.trim() || busy}
                      onClick={() =>
                        act(() =>
                          forceMode === "close"
                            ? ticketsApi.forceClose(id, forceNote.trim())
                            : ticketsApi.forceCancel(id, forceNote.trim())
                        ).then(() => {
                          setForceMode(null);
                          setForceNote("");
                        })
                      }
                    >
                      {forceMode === "close" ? "Force close" : "Force cancel"}
                    </button>
                    <button className="tk-btn-ghost" onClick={() => setForceMode(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {showSla && (
                  <div className="tk-sheet">
                    <span className="tk-sheet-title">Resolve by</span>
                    <input
                      type="datetime-local"
                      value={slaDate}
                      min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                      onChange={(e) => setSlaDate(e.target.value)}
                    />
                    <button
                      className="tk-btn-primary"
                      disabled={!slaDate || busy}
                      onClick={() =>
                        act(() =>
                          ticketsApi.setSla(id, {
                            due_at: new Date(slaDate).toISOString(),
                          })
                        ).then(() => {
                          setShowSla(false);
                          setSlaDate("");
                        })
                      }
                    >
                      Set deadline
                    </button>
                    <button className="tk-btn-ghost" onClick={() => setShowSla(false)}>
                      Cancel
                    </button>
                  </div>
                )}

                {showAssign && (
                  <div className="tk-sheet tk-sheet--assign">
                    <span className="tk-sheet-title">Assign to</span>
                    <div className="tk-staff-list">
                      {staff.length === 0 && (
                        <em>
                          No worker matches this category's trade. Assign it to
                          yourself or another FM instead.
                        </em>
                      )}
                      {staff.map((s) => (
                        <button
                          key={s.id}
                          disabled={busy}
                          onClick={() =>
                            act(() => ticketsApi.assign(id, s.id)).then(() =>
                              setShowAssign(false)
                            )
                          }
                        >
                          {s.is_self ? "Assign to me" : s.name} · {s.role_label}
                        </button>
                      ))}
                    </div>
                    {!allStaffLoaded && (
                      <button className="tk-btn-ghost" onClick={loadAllStaff}>
                        No suitable worker? Assign to self or another FM
                      </button>
                    )}
                    <button className="tk-btn-ghost" onClick={() => setShowAssign(false)}>
                      Cancel
                    </button>
                  </div>
                )}

                {showNotFixed && (
                  <div className="tk-sheet">
                    <span className="tk-sheet-title">Send back — what's still wrong?</span>
                    <input
                      value={notFixed}
                      onChange={(e) => setNotFixed(e.target.value)}
                      placeholder="What's still wrong?"
                    />
                    <button
                      className="tk-btn-primary"
                      disabled={!notFixed.trim() || busy}
                      onClick={() =>
                        act(() => ticketsApi.notFixed(id, notFixed.trim())).then(() => {
                          setShowNotFixed(false);
                          setNotFixed("");
                        })
                      }
                    >
                      Send back
                    </button>
                    <button
                      className="tk-btn-ghost"
                      onClick={() => setShowNotFixed(false)}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {showClose && (
                  <div className="tk-sheet">
                    <span className="tk-sheet-title">Close on behalf of resident</span>
                    <input
                      value={closeNote}
                      onChange={(e) => setCloseNote(e.target.value)}
                      placeholder="How was the resident's OK obtained? (call / WhatsApp)"
                    />
                    <button
                      className="tk-btn-primary"
                      disabled={!closeNote.trim() || busy}
                      onClick={() =>
                        act(() =>
                          ticketsApi.closeOnBehalf(id, closeNote.trim())
                        ).then(() => {
                          setShowClose(false);
                          setCloseNote("");
                        })
                      }
                    >
                      Close
                    </button>
                    <button className="tk-btn-ghost" onClick={() => setShowClose(false)}>
                      Cancel
                    </button>
                  </div>
                )}

                {showRate && (
                  <div className="tk-rate">
                    <div className="tk-rate-h">
                      Close ticket — rate how it was handled
                    </div>
                    <div className="tk-rate-row">
                      <span>Facility Manager</span>
                      <Stars value={fmStars} onChange={setFmStars} />
                    </div>
                    <div className="tk-rate-row">
                      <span>Worker {t.assignee ? `(${t.assignee.name})` : ""}</span>
                      <Stars value={workerStars} onChange={setWorkerStars} />
                    </div>
                    <div className="tk-rate-foot">
                      <button
                        className="tk-btn-ghost"
                        onClick={() => setShowRate(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="tk-btn-primary"
                        disabled={busy}
                        onClick={() =>
                          act(() =>
                            ticketsApi.confirm(id, {
                              fm_rating: fmStars || undefined,
                              worker_rating: workerStars || undefined,
                            })
                          ).then(() => setShowRate(false))
                        }
                      >
                        Close &amp; submit
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ---------- Details rail (sticky) ---------- */}
              <aside className="tk-rail">
                <div className="tk-rail-card">
                  <div className="tk-rail-row">
                    <span className="tk-k">Priority</span>
                    <span className="tk-v">
                      <Priority p={t.priority} />
                    </span>
                  </div>
                  {t.sla_due_at && (
                    <div className="tk-rail-row">
                      <span className="tk-k">Due</span>
                      <span className="tk-v">{fmt(t.sla_due_at)}</span>
                    </div>
                  )}
                  <div className="tk-rail-row">
                    <span className="tk-k">Assignee</span>
                    <span className="tk-v">
                      {t.assignee ? (
                        <Assignee assignee={t.assignee} />
                      ) : (
                        <span className="tk-person-empty">Unassigned</span>
                      )}
                    </span>
                  </div>
                  {t.availability && (
                    <div className="tk-rail-row">
                      <span className="tk-k">Available</span>
                      <span className="tk-v">{t.availability}</span>
                    </div>
                  )}
                  {(t.fm_rating || t.worker_rating) && (
                    <div className="tk-rail-row">
                      <span className="tk-k">Rated</span>
                      <span className="tk-v">
                        {t.fm_rating ? `FM ${t.fm_rating}★` : ""}
                        {t.fm_rating && t.worker_rating ? " · " : ""}
                        {t.worker_rating ? `Worker ${t.worker_rating}★` : ""}
                      </span>
                    </div>
                  )}
                </div>
              </aside>
            </div>

            <Lightbox src={zoom} onClose={() => setZoom(null)} />
            {showEdit && (
              <CreateTicketModal
                cats={cats}
                edit={t}
                priorityOnly={isFM && !isRaiser}
                onClose={() => setShowEdit(false)}
                onCreated={() => {
                  setShowEdit(false);
                  load();
                  onChanged && onChanged();
                }}
              />
            )}
          </>
        );
      })()}
      </div>
    </>
  );
}

// Named exports for the resident-facing MyTicketsPage (uses only the
// shared primitives — not the FM-specific controls).
export { CreateTicketModal, Phase, Priority, Stepper, Attachments, Lightbox };
